import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePosts } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { seoConfig } from '../../lib/seo';
import { Post } from '../../lib/supabase';
import LoadingSpinner from '../ui/LoadingSpinner';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import '../../styles/utilities/spacing.css';
import '../../styles/utilities/typography.css';

const Posts: React.FC = () => {
  useCanonicalUrl({
    ...seoConfig.home,
    title: 'All Posts | LUT Rolls',
    description: 'Explore all articles and updates about LUT Rolls features, photography tips, and app news.'
  });

  const navigate = useNavigate();
  const { data: posts, loading, error } = usePosts();

  // Filter only published and publicly visible posts
  const publishedPosts = useMemo(() => {
    return posts?.filter(post => post.is_published && post.public_visibility) || [];
  }, [posts]);

  // Group posts by category
  const postsByCategory = useMemo(() => {
    const grouped: { [key: string]: Post[] } = {};

    publishedPosts.forEach(post => {
      const category = post.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(post);
    });

    return grouped;
  }, [publishedPosts]);

  const handlePostClick = (post: Post) => {
    if (post.route) {
      navigate(post.route);
    }
  };

  // Breadcrumbs for navigation
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
    { label: 'All Posts', isActive: true }
  ];

  if (loading) {
    return (
      <div className="posts-page">
        <main className="posts-main">
          <div className="container">
            <LoadingSpinner />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="posts-page">
      <main className="posts-main pb-22">
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
          {/* Header */}
          <div style={{ marginBottom: '3rem' }}>
            <Breadcrumbs items={breadcrumbs} />
            <h1 style={{ fontSize: '3rem', marginBottom: '1rem', marginTop: '1rem' }}>All Posts</h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.8 }}>
              Explore all articles and updates about LUT Rolls features and photography tips.
            </p>
          </div>

          {error && publishedPosts.length === 0 && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Unable to load posts. Please try again later.</p>
            </div>
          )}

          {/* Posts by Category */}
          {Object.keys(postsByCategory).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
              {Object.entries(postsByCategory).map(([category, categoryPosts]) => (
                <div key={category}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    paddingBottom: '0.5rem'
                  }}>
                    {category}
                  </h2>
                  <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
                    {categoryPosts.map(post => (
                      <div
                        key={post.id}
                        className="card"
                        style={{
                          cursor: post.route ? 'pointer' : 'default',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          padding: '1.5rem'
                        }}
                        onClick={() => handlePostClick(post)}
                        onMouseEnter={(e) => {
                          if (post.route) {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{post.title}</h3>
                            {post.description && (
                              <p style={{ fontSize: '1rem', opacity: 0.7, lineHeight: 1.5, marginBlockEnd: '4px' }}>
                                {post.description}
                              </p>
                            )}
                          </div>
                          {post.route && (
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.5, flexShrink: 0, marginLeft: '1rem' }}>
                              <path
                                d="M6 4L10 8L6 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !loading && (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <p style={{ fontSize: '1.25rem', opacity: 0.7 }}>No posts available at the moment.</p>
              </div>
            )
          )}
        </div>
      </main>
    </div>
  );
};

export default Posts;